import { useCallback, useEffect, useRef, useState } from "react";

/**
 * One Strategy Report row that NO backend calculation produces: an
 * operator-typed figure that outlives the report's remount, dies on a page
 * load, and never crosses to another client.
 *
 * WHY NOT `useStrategyReportValue`, which every other row uses. That store is
 * one flat object with no client, account, period or ticker anywhere in its
 * key (useReportStorage), so a figure read back out of it belongs to whichever
 * report was last on screen. On a row a RUN can replace, that is survivable --
 * the next run overwrites it, and until then the field says which state it is
 * in. On a row no run can ever overwrite it is not: the figure an operator
 * typed for client A was still sitting there, in full figure weight, under
 * client B's name, and it reached client B's PDF as an ordinary result. That
 * is the same failure as the Keep card's stale figures (task 3155) and the
 * Beginning Concentration baseline (task 3141).
 *
 * WHY NOT PLAIN COMPONENT STATE EITHER, which is the trap here and the reason
 * this hook has the shape it does. `StrategyReportContent` renders inside an
 * @mui/lab `TabPanel` with no `keepMounted`, so the whole report UNMOUNTS on a
 * tab switch -- and the report page's window-level keydown toggles that tab on
 * a BARE TAB PRESS from anywhere, the value fields included (ReportPage). Held
 * in component state, every figure an operator had typed would vanish the
 * moment they pressed Tab to leave the field, silently and all at once. Every
 * localStorage-backed row survives that remount only because its hook
 * re-reads the store; a session-only row has nowhere to read back from.
 * useBeginningConcentration reached this same conclusion for the same
 * component and rejected plain state for the same keystroke.
 *
 * So: MODULE SCOPE, which is exactly the lifetime the answer asks for. It
 * outlives the remount, and a page load re-evaluates this module and returns
 * every row to its seed.
 *
 * SCOPED TO ONE REPORT. `scopeKey` identifies whose card this is, and a
 * figure held for a different scope is dropped rather than shown -- the same
 * rule, and the same reason, as the account scoping on Beginning
 * Concentration: a figure typed for one client is not a claim about the next
 * one, and it would otherwise reach that client's PDF as their own.
 *
 * That key must be STABLE ACROSS THE REMOUNT above and live on a real client
 * change, which is why its caller builds it from the SETTINGS STORE rather
 * than from `settingsInfo`: the settings context is created inside the same
 * unmounting panel and starts empty on every remount, so keying on that would
 * read as a client change on every tab switch and undo the whole point of the
 * module holder.
 *
 * The seed stays the CALLER's: STRATEGY_REPORT_DEFAULTS is the single home for
 * what every row of this report shows before a figure replaces it, and a
 * private seed table in here is the copy that drifts from it. `key` is the
 * row's own storage key, reused verbatim so the two hooks name a row the same
 * way -- it identifies the row in the holder below and is no longer a
 * localStorage key at all.
 */

// One holder for every uncalculated row, keyed by scope AND row so a figure
// can never be read back under a scope it was not typed for. A Map rather
// than a variable per row: the set of rows is the caller's to decide, and
// this file should not have to change when one is added.
const typedFigures = new Map<string, number | string>();

const holderKey = (scopeKey: string, key: string) => `${scopeKey}::${key}`;

/** Test seam -- a page load is what clears this in the browser. */
export const resetUncalculatedReportValues = () => {
  typedFigures.clear();
};

export const useUncalculatedReportValue = (
  key: string,
  seed: number | string,
  scopeKey: string,
): [number | string, (value: number | string) => void] => {
  const [value, setValue] = useState<number | string>(() => {
    const held = typedFigures.get(holderKey(scopeKey, key));
    return held === undefined ? seed : held;
  });

  // Which scope THIS hook last showed a figure for. Per-hook rather than
  // shared, and that is load-bearing: ten rows run this effect on the same
  // scope change, and a single shared marker would let the first one to run
  // satisfy the guard for the other nine, leaving their previous figures on
  // screen under the new client's name.
  const shownScope = useRef(scopeKey);

  useEffect(() => {
    if (shownScope.current === scopeKey) return;
    shownScope.current = scopeKey;
    // Whatever this row is showing belongs to the report we have just left.
    // Show this scope's own figure if one was typed, otherwise the seed.
    const held = typedFigures.get(holderKey(scopeKey, key));
    setValue(held === undefined ? seed : held);
    // Drop the scopes nobody is looking at any more, so a long session moving
    // between clients does not accumulate their figures in memory. Done on
    // the change rather than on every set: it is the only moment the set of
    // live scopes actually shrinks.
    Array.from(typedFigures.keys())
      .filter((entry) => !entry.startsWith(`${scopeKey}::`))
      .forEach((entry) => typedFigures.delete(entry));
  }, [scopeKey, key, seed]);

  const set = useCallback(
    (next: number | string) => {
      typedFigures.set(holderKey(scopeKey, key), next);
      setValue(next);
    },
    [key, scopeKey],
  );

  return [value, set];
};
